import { useState, useEffect, useCallback } from 'react';
import qs from 'qs';

export interface UseHashLocationResult {
  navigate: ChangeHashLocation;
  values: Record<string, string>;
  cleanKeys: (vals: Record<string, UrlHashItemValue>) => string;
}

export type ChangeHashLocation = (values: Record<string, UrlHashItemValue>) => void;

export type UrlHashItemValue =
  | string
  | number
  | string[]
  | number[]
  | boolean
  | Record<string, string[]>;

export function currentUrlHash(): string {
  if (typeof window === 'undefined') {
    // SSR handling
    return '';
  }
  if (window.location.hash?.includes('#')) {
    return (
      decodeURIComponent(window.location.hash.replace(/^#/, '')).replace(/\?utm_/, '&utm_') || ''
    );
  }
  return '';
}

export default function useHashLocation(): UseHashLocationResult {
  // The window.location.hash is the source of truth.
  // `navigate` exposes a way to change the hash, given a parsed object
  // When the hash changes, we parse it into the returned `value` object

  const [internalHash, setInternalHash] = useState<string>(currentUrlHash());
  const setUrlHash = useCallback((to: string) => {
    if (to === currentUrlHash()) return; // dont set if equal to current

    if (to) {
      window.location.hash = to;
    } else {
      window.history.replaceState(null, '', ' '); // removes # from the URL
    }
  }, []);

  // when the hash changes, set it in internal state so it can be parsed and returned
  const handleUrlHashChange = useCallback(() => {
    if (internalHash === currentUrlHash()) return; // skip if already set to same value
    setInternalHash(currentUrlHash());
  }, [internalHash]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', handleUrlHashChange);
    }
    return (): void => {
      window.removeEventListener('hashchange', handleUrlHashChange);
    };
  }, [handleUrlHashChange]);

  // handle back button
  if (currentUrlHash() !== internalHash) setInternalHash(currentUrlHash());

  // parse the internal state hash
  let values: Record<string, string> = {};
  if (internalHash) {
    values = qs.parse(internalHash) as Record<string, string>;
  }

  function cleanKeys(vals: Record<string, UrlHashItemValue>): string {
    const cleaned: Record<string, UrlHashItemValue> = {};
    Object.entries(vals)
      .sort()
      .forEach(([key, val]) => {
        if (vals[key] !== undefined && vals[key] !== '') {
          cleaned[key] = val;
        }
      });
    return qs.stringify(cleaned, { arrayFormat: 'brackets', encode: false });
  }

  // this exposes a way to change the hash, given a parsed object
  const navigate = useCallback(
    (vals: Record<string, UrlHashItemValue>) => {
      const cleaned = cleanKeys(vals);
      setUrlHash(cleaned);
    },
    [setUrlHash]
  );

  return {
    values,
    navigate,
    cleanKeys,
  };
}
